import React, { useState } from 'react';
import { formatInTimezone } from '../../utils/formatInTimezone';
import { formatDuration } from '../../utils/formatDuration';
import './CameraDiagnostics.css';

const pick = (obj, ...keys) => {
  for (const k of keys) {
    if (obj && obj[k] != null) return obj[k];
  }
  return null;
};

const Row = ({ label, value }) => (
  <div className="diag-row d-flex justify-content-between">
    <span className="text-muted">{label}</span>
    <span>{value ?? '—'}</span>
  </div>
);

const CameraDiagnosticsPanel = ({ connection, displayTimezone, actionsSlot }) => {
  const [showTech, setShowTech] = useState(false);
  const c = connection || {};
  const incident = c.incident || {};
  const retry = c.retry || {};
  const email = c.email || {};
  const recovery = c.last_recovery || c.recovery || {};

  const fdt = (v) => formatInTimezone(v, displayTimezone);
  // null/undefined значит «бэкенд не прислал данных», это не то же самое, что
  // «письмо не отправлено», поэтому такие поля показываем прочерком.
  const yesNo = (v) => (v == null ? null : (v ? 'Да' : 'Нет'));

  // Бэкенд отдаёт признаки писем внутри incident (offline_email_sent*) и
  // last_recovery (restored_email_sent*), отдельного блока email в ответе нет.
  // Плоские ключи и c.email оставлены для совместимости со старым форматом.
  const offlineSent = email.offline_sent ?? pick(incident, 'offline_email_sent') ?? pick(c, 'offline_email_sent');
  const offlineSentAt =
    pick(email, 'offline_sent_at') ?? pick(incident, 'offline_email_sent_at') ?? pick(c, 'offline_email_sent_at');
  const recoverySent =
    email.recovery_sent ?? pick(recovery, 'restored_email_sent', 'recovery_email_sent') ?? pick(c, 'recovery_email_sent');
  const recoverySentAt =
    pick(email, 'recovery_sent_at') ??
    pick(recovery, 'restored_email_sent_at', 'recovery_email_sent_at') ??
    pick(c, 'recovery_email_sent_at');

  return (
    <div className="camera-diagnostics">
      {actionsSlot && <div className="diag-actions mb-3">{actionsSlot}</div>}

      <section className="diag-block mb-3">
        <h6>Текущее состояние</h6>
        <Row label="Статус соединения" value={pick(c, 'connection_status')} />
        <Row label="Должна работать" value={c.desired_running == null ? '—' : (c.desired_running ? 'Да' : 'Нет')} />
        <Row label="Статус камеры" value={pick(c, 'camera_status', 'status')} />
        <Row label="Последний кадр" value={fdt(pick(c, 'last_frame_at', 'last_frame_time'))} />
      </section>

      <section className="diag-block mb-3">
        <h6>Активный инцидент</h6>
        <Row label="Начат" value={fdt(pick(incident, 'started_at') ?? pick(c, 'started_at', 'offline_since'))} />
        <Row
          label="Длительность"
          value={formatDuration(
            pick(incident, 'duration_seconds', 'incident_duration_seconds') ?? pick(c, 'offline_duration_seconds')
          )}
        />
        <Row label="Причина" value={pick(incident, 'failure_message') ?? pick(c, 'failure_message')} />
        <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setShowTech((v) => !v)}>
          {showTech ? 'Скрыть тех. детали' : 'Тех. детали'}
        </button>
        {showTech && (
          <div className="diag-tech small text-muted mt-1">
            <Row label="failure_code" value={pick(incident, 'failure_code') ?? pick(c, 'failure_code')} />
            <Row label="incident_id" value={pick(incident, 'incident_id', 'id') ?? pick(c, 'incident_id')} />
          </div>
        )}
      </section>

      <section className="diag-block mb-3">
        <h6>Retry</h6>
        <Row label="Попыток" value={pick(retry, 'count', 'retry_count') ?? pick(c, 'retry_count')} />
        <Row label="Последняя попытка" value={fdt(pick(retry, 'last_attempt_at') ?? pick(c, 'last_attempt_at'))} />
        <Row label="Последняя ошибка" value={fdt(pick(retry, 'last_failure_at') ?? pick(c, 'last_failure_at'))} />
        <Row label="Следующая попытка" value={fdt(pick(retry, 'next_retry_at') ?? pick(c, 'next_retry_at'))} />
      </section>

      <section className="diag-block mb-3">
        <h6>Email-уведомления</h6>
        <Row label="Offline отправлено" value={yesNo(offlineSent)} />
        <Row label="Offline время" value={fdt(offlineSentAt)} />
        <Row label="Recovery отправлено" value={yesNo(recoverySent)} />
        <Row label="Recovery время" value={fdt(recoverySentAt)} />
      </section>

      <section className="diag-block">
        <h6>Последнее восстановление</h6>
        <Row
          label="Восстановлена"
          value={fdt(pick(recovery, 'restored_at') ?? pick(retry, 'last_success_at') ?? pick(c, 'last_success_at'))}
        />
        <Row label="Длительность простоя" value={formatDuration(pick(recovery, 'duration_seconds'))} />
        <Row
          label="Число попыток при восстановлении"
          value={pick(recovery, 'retry_count') ?? pick(c, 'recovery_retry_count')}
        />
      </section>
    </div>
  );
};

export default CameraDiagnosticsPanel;
